import { Expense, PurchaseOrderStatusEnum, RightEnum, TabTypeEnum } from "@finway-group/shared/lib/models"
import { Tabs } from "antd"
import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { FilterSectionLegacy } from "~Components/filter/FilterSectionLegacy"
import { FilterSectionOptionsInterface } from "~Components/filter/filterTypes"
import Loading from "~Components/loading"
import { PageTitle } from "~Components/page/pageTitle"
import { useSelectedExpenses } from "~Shared/context/selectedExpenses.context"
import { useCostCenters } from "~Shared/hooks/costCenter.hooks"
import { useCostCenters2 } from "~Shared/hooks/costCenter2.hooks"
import { useEmployees } from "~Shared/hooks/employee.hooks"
import { useActiveTab, useExpenseCounts, useInboxInvoicesCount, useRequestActionDisabled } from "~Shared/hooks/expense.hooks"
import { useExpenseAccounts } from "~Shared/hooks/expenseAccount.hooks"
import { useExpenseTags } from "~Shared/hooks/expenseTags.hooks"
import { applyFilter } from "~Shared/hooks/filter.hooks"
import { getTableFilterQueryStringStore, useTable, useTableFilterObject, useTableFilterPresets, useTableSearchString } from "~Shared/hooks/table.hooks"
import { useUserCreditors } from "~Shared/hooks/vendor.hooks"
import { convertSortOrder } from "~Shared/queries/query.utils"
import { AuthzService } from "~Shared/services"
import { fetchArchivedExpensesCounts, fetchDashboardExpensesCounts, setActiveTab as setActiveTabAction } from "~Shared/store/actions/expense/expenseActions"
import { ExpenseCountInterface } from "~Shared/store/actions/expense/expenseTypes"
import { updateDashboardSearch, updateTableSearch } from "~Shared/store/actions/tables/tableActions"
import { TablesEnum } from "~Shared/store/reducers/tableConfigReducer"
import { lazily } from "~Shared/utils/react.utils"
import { InProgressTab } from "../expenses/tabs/inProgressTab"
import { procurementColumnMapper } from "../purchaseOrders/columns/ProcurementColumns"
import { usePurchaseOrders } from "../purchaseOrders/hooks/usePurchaseOrders.hook"
import { ProcurementTab } from "../purchaseOrders/tabs/ProcurementTab"
import { DashboardActionButtons } from "./dashboardActionButtons"

const { InboxInvoiceTab } = lazily(() => import("../expenses/tabs/inboxInvoicesTab"))
const { DoneOrAllTab } = lazily(() => import("../expenses/tabs/doneOrAllTab"))
const { ReviewTab } = lazily(() => import("../expenses/tabs/ReviewTab"))
const { TodoTab } = lazily(() => import("../expenses/tabs/todoTab"))
const { ToPayAndExportTab } = lazily(() => import("../expenses/tabs/ToPayAndExportTab"))
const { ArchiveTab } = lazily(() => import("../expenses/tabs/archiveTab"))

const { TabPane } = Tabs

export const DashboardContainer = () => {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const activeTab = useActiveTab()
    const userCreditors = useUserCreditors(AuthzService.isRightGrantedForLoggedInUser(RightEnum.EMPLOYEE__ALL__READ))
    const costCenters = useCostCenters(true)
    const employees = useEmployees()
    const expenseTags = useExpenseTags()
    const costCenters2 = useCostCenters2()
    const expenseAccounts = useExpenseAccounts()
    const filterSetRef = useRef(false)
    const filterObject = useTableFilterObject(TablesEnum.TODO_INVOICE_APPROVAL)

    const [selectedExpenses, setSelectedExpenses] = useSelectedExpenses()
    const [filterOptions, setFilterOptions] = useState<FilterSectionOptionsInterface>({})
    const isGlobalApprover = AuthzService.isLoggedInUserGlobalApprover()
    const hasReadRightForExpensesToPay = AuthzService.canLoggedInUserSeeExpensesToPay()
    const hasReadRightForExpensesToExport = AuthzService.canLoggedInUserSeeExpensesToExport()
    const hasReadRightForExpensesToReview = AuthzService.canLoggedInUserSeeExpensesToReview()
    const hasReadRightForProcurement = AuthzService.canLoggedInUserSeePurchseOrderProcurement()

    // Loading for the entire dashboard. Awaits that the document counts are updated to render the tabs
    const [dashboardLoading, setDashboardLoading] = useState(false)

    const dashboardExpenseCounts = useExpenseCounts()

    const requestActionDisabled = useRequestActionDisabled()
    const inboxInvoicesCount = useInboxInvoicesCount()

    useEffect(() => {
        const updated = { expenseTags, costCenters, employees, userCreditors, costCenters2, expenseAccounts }
        if (JSON.stringify(updated) !== JSON.stringify(filterOptions)) {
            setFilterOptions({ expenseTags, costCenters, employees, userCreditors, costCenters2, expenseAccounts })
        }
    }, [expenseTags, costCenters, employees, userCreditors, costCenters2, expenseAccounts])

    const tableEnum = useMemo(() => {
        switch (activeTab) {
            case TabTypeEnum.ARCHIVE:
                return TablesEnum.ARCHIVE
            case TabTypeEnum.DONE:
                return TablesEnum.DONE_REQUESTS
            case TabTypeEnum.INBOX_INVOICES:
                return TablesEnum.INBOX_INVOICE
            case TabTypeEnum.IN_PROGRESS:
                return TablesEnum.IN_PROGRESS
            case TabTypeEnum.PAY_EXPORT:
                return TablesEnum.PAY_AND_EXPORT_TO_BE_EXPORTED
            case TabTypeEnum.REVIEW:
                return TablesEnum.REVIEW_REQUESTS
            default:
                return TablesEnum.TODO_DOCS_NEEDED
        }
    }, [activeTab])

    const {
        serverData: procurementData,
        handleSearch: handleProcurementSearch,
        pagination,
        setCurrentPage,
        setPageSize,
        isTableEmpty,
        isNoSearchDataFound,
        setSortField,
        setSortOption,
        resetSorting,
    } = usePurchaseOrders({
        "state.currentState": { in: [PurchaseOrderStatusEnum.PROCUREMENT] },
    })
    const dashboardSearchString = useTableSearchString(tableEnum)
    useEffect(() => {
        handleProcurementSearch(dashboardSearchString)
    }, [dashboardSearchString, handleProcurementSearch])

    // Get current filter presets for the active table
    const currentFilterPresets = useTableFilterPresets(tableEnum)

    // Create a memoized filter for purchase order todos based on quick filter
    const purchaseOrderTodoFilter = useMemo(() => {
        const baseFilter = { "state.currentState": { in: [PurchaseOrderStatusEnum.WAITING_FOR_APPROVAL] } }

        // If there are active presets, add them to the filter
        if (currentFilterPresets.length > 0) {
            return {
                ...baseFilter,
                presets: { in: currentFilterPresets },
            }
        }

        return baseFilter
    }, [currentFilterPresets])

    // Update the usePurchaseOrders hook to use the dynamic filter
    const {
        serverData: purchaseOrderTodoData,
        handleSearch: handlePurchaseOrderSearch,
        pagination: purchaseOrderTodoPagination,
        setCurrentPage: setPurchaseOrderTodoCurrentPage,
        setPageSize: setPurchaseOrderTodoPageSize,
    } = usePurchaseOrders(purchaseOrderTodoFilter)

    useEffect(() => {
        handlePurchaseOrderSearch(dashboardSearchString)
    }, [dashboardSearchString, handlePurchaseOrderSearch])

    const archiveTable = useTable<Expense>(TablesEnum.ARCHIVE)

    const actionBtnState = useMemo(
        () => ({
            enableToDoRequestCreation: !requestActionDisabled,
            enableToDoExport: !requestActionDisabled,
            enableReviewed: !!selectedExpenses.length,
            enablePayExport: !!selectedExpenses.length,
        }),
        [requestActionDisabled, selectedExpenses.length],
    )

    const handleTabClick = (key: string) => {
        setSelectedExpenses([])
        if (key !== activeTab) {
            dispatch(setActiveTabAction(key))
        }
    }

    const handleDashboardSearch = useCallback(
        (value: string) => {
            setDashboardLoading(true)
            if (activeTab === TabTypeEnum.ARCHIVE) {
                dispatch(updateTableSearch(TablesEnum.ARCHIVE, value))
                dispatch(fetchArchivedExpensesCounts())
            } else {
                dispatch(updateDashboardSearch(value))
            }
            setDashboardLoading(false)
        },
        [dispatch, activeTab],
    )

    const getDataCount = (tableEnum: TabTypeEnum, data: ExpenseCountInterface) => {
        if (tableEnum === TabTypeEnum.IN_PROGRESS) {
            return data.inProgress
        }
        if (tableEnum === TabTypeEnum.PAY_EXPORT) {
            return data.payExport
        }
        if (tableEnum === TabTypeEnum.ARCHIVE) {
            return data.archive
        }
        if (tableEnum === TabTypeEnum.REVIEW) {
            return data.review
        }
        if (tableEnum === TabTypeEnum.TO_DOS) {
            return data.toDo + (purchaseOrderTodoPagination.total ?? 0)
        }
        if (tableEnum === TabTypeEnum.DONE) {
            return data.done
        }
        if (tableEnum === TabTypeEnum.ALL) {
            return data.allRequests
        }
        return 0
    }

    useEffect(() => {
        if (filterSetRef.current === false && Object.keys(filterObject).length > 0) {
            dispatch(fetchDashboardExpensesCounts())
            filterSetRef.current = true
        }
    }, [JSON.stringify(filterObject)])

    return (
        <div>
            <PageTitle titleKey={"view:dashboard.title"}>
                <DashboardActionButtons
                    table={tableEnum}
                    state={actionBtnState}
                    searchString={dashboardSearchString}
                    onSearch={handleDashboardSearch}
                    onFilter={(data) => {
                        setSelectedExpenses([])
                        return applyFilter(tableEnum, data)
                    }}
                    options={filterOptions}
                />
            </PageTitle>
            <div className={dashboardLoading ? "block" : "hidden"}>
                <Loading />
            </div>
            <div className={dashboardLoading ? "hidden" : "block"}>
                <Tabs
                    id="dashboard-tabs"
                    destroyInactiveTabPane={true}
                    defaultActiveKey={TabTypeEnum.TO_DOS}
                    activeKey={activeTab}
                    onTabClick={handleTabClick}
                    className="animation-appear pb-10 flex flex-col"
                >
                    <TabPane tab={`${t("label:invoice_inbox")} | ${inboxInvoicesCount}`} key={TabTypeEnum.INBOX_INVOICES}>
                        <Suspense fallback={<Loading />}>
                            <InboxInvoiceTab />
                        </Suspense>
                    </TabPane>

                    <TabPane tab={`${t("label:to_dos")} | ${getDataCount(TabTypeEnum.TO_DOS, dashboardExpenseCounts)}`} key={TabTypeEnum.TO_DOS}>
                        <FilterSectionLegacy table={TablesEnum.TODO_DOCS_NEEDED} options={filterOptions} />

                        <Suspense fallback={<Loading />}>
                            <TodoTab
                                purchaseOrderTodoData={purchaseOrderTodoData}
                                purchaseOrderPagination={purchaseOrderTodoPagination}
                                purchaseOrderOnTableChange={(paginationChange) => {
                                    if (paginationChange.current) {
                                        setPurchaseOrderTodoCurrentPage(paginationChange.current)
                                    }
                                    if (paginationChange.pageSize) {
                                        setPurchaseOrderTodoPageSize(paginationChange.pageSize)
                                    }
                                }}
                            />
                        </Suspense>
                    </TabPane>

                    {hasReadRightForProcurement ? (
                        <TabPane tab={`${t("label:procurement")} | ${pagination.total}`} key={TabTypeEnum.PROCUREMENT}>
                            <Suspense fallback={<Loading />}>
                                <ProcurementTab
                                    procurementData={procurementData ?? []}
                                    pagination={pagination}
                                    isTableEmpty={isTableEmpty}
                                    isNoSearchDataFound={isNoSearchDataFound}
                                    setCurrentPage={setCurrentPage}
                                    setPageSize={setPageSize}
                                    setSorting={({ field, order }) => {
                                        if (field && order) {
                                            setSortField(procurementColumnMapper[field as keyof typeof procurementColumnMapper]!)
                                            setSortOption(convertSortOrder(order))
                                        } else {
                                            resetSorting()
                                        }
                                    }}
                                />
                            </Suspense>
                        </TabPane>
                    ) : null}
                    {!isGlobalApprover && (
                        <TabPane tab={`${t("label:in_progress")} | ${getDataCount(TabTypeEnum.IN_PROGRESS, dashboardExpenseCounts)}`} key={TabTypeEnum.IN_PROGRESS}>
                            <FilterSectionLegacy table={TablesEnum.IN_PROGRESS} options={filterOptions} />

                            <Suspense fallback={<Loading />}>
                                <InProgressTab />
                            </Suspense>
                        </TabPane>
                    )}
                    {hasReadRightForExpensesToReview && (
                        <TabPane tab={`${t("label:review")} | ${getDataCount(TabTypeEnum.REVIEW, dashboardExpenseCounts)}`} key={TabTypeEnum.REVIEW}>
                            <FilterSectionLegacy table={TablesEnum.REVIEW_REQUESTS} options={filterOptions} />

                            <Suspense fallback={<Loading />}>
                                <ReviewTab />
                            </Suspense>
                        </TabPane>
                    )}
                    {(hasReadRightForExpensesToPay || hasReadRightForExpensesToExport) && (
                        <TabPane tab={`${t("label:pay_and_export")} | ${getDataCount(TabTypeEnum.PAY_EXPORT, dashboardExpenseCounts)}`} key={TabTypeEnum.PAY_EXPORT}>
                            <FilterSectionLegacy table={TablesEnum.PAY_AND_EXPORT_TO_BE_EXPORTED} options={filterOptions} />

                            <Suspense fallback={<Loading />}>
                                <ToPayAndExportTab />
                            </Suspense>
                        </TabPane>
                    )}
                    <TabPane tab={`${t("label:done")} | ${getDataCount(TabTypeEnum.DONE, dashboardExpenseCounts)}`} key={TabTypeEnum.DONE}>
                        <FilterSectionLegacy table={TablesEnum.DONE_REQUESTS} options={filterOptions} />

                        <Suspense fallback={<Loading />}>
                            <DoneOrAllTab tablesEnum={TablesEnum.DONE_REQUESTS} />
                        </Suspense>
                    </TabPane>
                    {isGlobalApprover && (
                        <TabPane tab={`${t("label:all")} | ${getDataCount(TabTypeEnum.ALL, dashboardExpenseCounts)}`} key={TabTypeEnum.ALL}>
                            <FilterSectionLegacy table={TablesEnum.ALL_REQUESTS} options={filterOptions} />

                            <Suspense fallback={<Loading />}>
                                <DoneOrAllTab tablesEnum={TablesEnum.ALL_REQUESTS} />
                            </Suspense>
                        </TabPane>
                    )}
                    <TabPane
                        tab={
                            getTableFilterQueryStringStore(TablesEnum.ARCHIVE) && !archiveTable.isFetching
                                ? `${t("label:archive")} | ${getDataCount(TabTypeEnum.ARCHIVE, dashboardExpenseCounts)}`
                                : t("label:archive")
                        }
                        key={TabTypeEnum.ARCHIVE}
                    >
                        <FilterSectionLegacy table={TablesEnum.ARCHIVE} options={{ ...filterOptions, activeTab: TabTypeEnum.ARCHIVE }} />

                        <Suspense fallback={<Loading />}>
                            <ArchiveTab />
                        </Suspense>
                    </TabPane>
                </Tabs>
            </div>
        </div>
    )
}
